// src/contexts/EventContext.js
import React, { createContext, useState, useContext } from 'react';
import apiService from '../services/apiService';

// Function to transform event data (e.g., renaming _id to id)
const transformEvents = (events) => {
  if (!Array.isArray(events)) return [];
  return events.map((event) => {
    const { _id, ...rest } = event;
    return { id: _id, ...rest };
  });
};

const transformEvent = (pro) => {
  const { _id, ...rest } = pro;
  return { id: _id, ...rest };
};

// Create the context
const EventContext = createContext();

// Create a custom hook for easier access to the context
export const useEvent = () => {
  return useContext(EventContext);
};

// Export the provider to wrap around the app
export const EventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);

  // Function to add a new event
  const addEvent = async (event) => {
    const {
      content,
      end_day,
      start_day,
      project: { id },
    } = event;
    const { data } = await apiService.post('/api/v1/work-plan', {
      project: id,
      content,
      end_day,
      start_day,
    });
    const user = events[0]?.user;
    setEvents((prevEvents) => [
      ...prevEvents,
      transformEvent({ ...data, user: user, project: event?.project }),
    ]);
  };

  // Function to remove an event by id
  const removeEvent = async (id) => {
    try {
      await apiService.delete(`/api/v1/work-plan/${id}`);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
    setEvents((prevEvents) => prevEvents.filter((event) => event.id !== id));
  };

  // Function to update an event
  const updateEvent = async (params) => {
    try {
      const { data } = await apiService.patch(
        `/api/v1/work-plan/${params.id}`,
        { params }
      );
      setEvents((prevEvents) =>
        prevEvents.map((event) => (event.id === data._id ? params : event))
      );
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  // Function to fetch events (could be from an API)
  const fetchEvents = async () => {
    try {
      const { data } = await apiService.get('/api/v1/work-plan');
      setEvents(transformEvents(data || []));
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };
  const fetchEventsByProject = async (params) => {
    try {
      const { data } = await apiService.get('/api/v1/work-plan', { params });
      setEvents(transformEvents(data || []));
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  // Function to get an event by id
  const getEventById = (id) => {
    return events.find((event) => event.id === id) || null;
  };

  return (
    <EventContext.Provider
      value={{
        events,
        addEvent,
        removeEvent,
        updateEvent,
        fetchEvents,
        getEventById,
        fetchEventsByProject,
      }}>
      {children}
    </EventContext.Provider>
  );
};
